import React, { useState, useEffect } from 'react';
import {
  StatType,
  StatTypeUpdateInput,
  STAT_TYPE_CATEGORIES,
  STAT_TYPE_EVENT_RESULTS,
} from '../../services/statTypesService';

interface EditStatTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: StatTypeUpdateInput) => Promise<void>;
  statType: StatType | null;
}

const EditStatTypeModal: React.FC<EditStatTypeModalProps> = ({
  isOpen,
  onClose,
  onSave,
  statType,
}) => {
  const [formData, setFormData] = useState<StatTypeUpdateInput>({
    category: '',
    stat_key: '',
    display_name: '',
    short_description: null,
    event_result: '',
    is_active: true,
    sort_order: 0,
    emoji: null,
    is_calculated: false,
    visible_in_reports: null,
    top_stats: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (statType) {
      setFormData({
        category: statType.category,
        stat_key: statType.stat_key,
        display_name: statType.display_name,
        short_description: statType.short_description ?? null,
        event_result: statType.event_result,
        is_active: statType.is_active ?? true,
        sort_order: statType.sort_order ?? 0,
        emoji: statType.emoji ?? null,
        is_calculated: statType.is_calculated ?? false,
        visible_in_reports: statType.visible_in_reports ?? null,
        top_stats: statType.top_stats ?? null,
      });
      setErrors({});
    }
  }, [statType]);

  const handleChange = (field: keyof StatTypeUpdateInput, value: string | number | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!formData.category?.trim()) e.category = 'Category is required';
    if (!formData.stat_key?.trim()) e.stat_key = 'Stat key is required';
    if (formData.stat_key && !/^[a-z0-9_]+$/.test(formData.stat_key.trim())) {
      e.stat_key = 'Stat key must be lowercase letters, numbers and underscores only';
    }
    if (!formData.display_name?.trim()) e.display_name = 'Display name is required';
    if (!formData.event_result?.trim()) e.event_result = 'Event result is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error('Error updating stat type:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !statType) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Stat Type</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STAT_TYPE_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stat key *</label>
            <input
              type="text"
              value={formData.stat_key}
              onChange={(e) => handleChange('stat_key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.stat_key && <p className="text-red-500 text-xs mt-1">{errors.stat_key}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Display name *</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => handleChange('display_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.display_name && <p className="text-red-500 text-xs mt-1">{errors.display_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Short description</label>
            <input
              type="text"
              value={formData.short_description ?? ''}
              onChange={(e) => handleChange('short_description', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event result *</label>
            <select
              value={formData.event_result}
              onChange={(e) => handleChange('event_result', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STAT_TYPE_EVENT_RESULTS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
            <input
              type="text"
              value={formData.emoji ?? ''}
              onChange={(e) => handleChange('emoji', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active ?? true}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_calculated ?? false}
                onChange={(e) => handleChange('is_calculated', e.target.checked)}
              />
              <span className="text-sm text-gray-700">Calculated</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.visible_in_reports === true}
                onChange={(e) => handleChange('visible_in_reports', e.target.checked ? true : null)}
              />
              <span className="text-sm text-gray-700">Visible in reports</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.top_stats === true}
                onChange={(e) => handleChange('top_stats', e.target.checked ? true : null)}
              />
              <span className="text-sm text-gray-700">Top stats</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort order</label>
            <input
              type="number"
              value={formData.sort_order ?? 0}
              onChange={(e) => handleChange('sort_order', parseInt(e.target.value, 10) ?? 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStatTypeModal;
